// Video creator (Director de Estudio / asistente) — migrado de n8n
// (`webhook/generar_video_creator`) al atlas-backend `/v1/videos/creator`
// SOLO para ref_mode 'none' | 'reference' | 'frames' | 'dialogo' (ADR-584).
// `dialogo_movimiento` y `ugc` NO están migrados: siguen a n8n SIEMPRE,
// incluso con el flag activo (ruteo parcial por ref_mode acá adentro).
//
// Contrato de éxito idéntico en ambos: `{response:'success', path}` con el
// path relativo a `video-project-assets` (n8n legacy puede devolver
// `video_id` de imagenes_generadas en su lugar — el caller decide).
//
// Clasificación de errores (importa para la carrera del asistente):
//  • `GenerateAdError` = fallo DEFINITIVO del backend (402 billing, 500
//    tipado, respuesta de error explícita) → no hay video que esperar.
//  • `Error` plano = fallo TÉCNICO (timeout / red / 202 wait vencido) → el
//    video puede haberse generado igual; un poll puede recuperarlo.

import { atlasBackendFetch, pollJobUntilSettled } from './atlasBackend';
import { N8N_ONLY_REF_MODES } from './creatorRetry';
import {
  GENERATE_AUTH_TOKEN,
  GENERATE_VIDEO_CREATOR_URL,
  USE_ATLAS_BACKEND,
} from '../config';
import {
  GenerateAdError,
  mapErrorTypeToMessage,
  type GenerateAdErrorType,
} from './generateAdRequest';

const CREATOR_TIMEOUT_MS = 300_000;
// Tope del poll post-202: tiene que cubrir el tope del provider de Seedance 2.5
// (~30 min desde 2026-08-31 — la cola GPU de Enhancor puede demorar incluso un
// video corto) más el wait inicial de ~4,7 min.
const CREATOR_PENDING_POLL_MS = 30 * 60 * 1000;

export interface VideoCreatorResult {
  /** Path relativo a `video-project-assets` (éxito normal). */
  path: string | null;
  /** Id de fila en `imagenes_generadas` (solo formato viejo de n8n). */
  videoId: string | null;
  /** true = atlas respondió 202 (wait interno de 280s vencido): el video
   *  SIGUE generándose. El caller decide (poll por execution_id, o mensaje
   *  de "tardó demasiado"). */
  pending: boolean;
}

export async function generateVideoCreator(
  body: Record<string, unknown>,
): Promise<VideoCreatorResult> {
  const useAtlas =
    USE_ATLAS_BACKEND && !N8N_ONLY_REF_MODES.has(String(body.ref_mode ?? ''));

  let res: Response;
  try {
    if (useAtlas) {
      const { user_id: _u, company_id: _c, ...atlasBody } = body;
      void _u;
      void _c;
      res = await atlasBackendFetch('/v1/videos/creator', atlasBody, {
        signal: AbortSignal.timeout(CREATOR_TIMEOUT_MS),
      });
    } else {
      res = await fetch(GENERATE_VIDEO_CREATOR_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(CREATOR_TIMEOUT_MS),
      });
    }
  } catch (e: any) {
    if (e?.name === 'TimeoutError' || e?.name === 'AbortError') {
      throw new Error('La generación de video tardó demasiado. Probá de nuevo.');
    }
    throw new Error('No se pudo conectar. Revisá tu conexión.');
  }

  if (useAtlas && res.status === 202) {
    // El wait del backend venció pero el job SIGUE corriendo: antes acá se
    // devolvía pending y el flujo quedaba ciego (el creator no escribe filas en
    // imagenes_generadas — si el job fallaba después, el usuario no se enteraba
    // nunca). Ahora se pollea el status del job hasta que termine.
    const pendingBody: any = await res.json().catch(() => null);
    const jobId = pendingBody?.job_id ? String(pendingBody.job_id) : null;
    if (jobId) {
      const settled = await pollJobUntilSettled(jobId, {
        timeoutMs: CREATOR_PENDING_POLL_MS,
      });
      if (settled?.status === 'succeeded') {
        const out: any = settled.output ?? {};
        if (typeof out.path === 'string' && out.path) {
          return {
            path: out.path,
            videoId: out.video_id != null ? String(out.video_id) : null,
            pending: false,
          };
        }
      }
      if (settled?.status === 'failed') {
        const t = (settled.errorType as GenerateAdErrorType) || 'technical';
        throw new GenerateAdError(
          t,
          settled.userMessage ?? mapErrorTypeToMessage(t),
          settled.errorType,
        );
      }
    }
    return { path: null, videoId: null, pending: true };
  }

  // n8n a veces responde 200 con cuerpo vacío o no-JSON cuando el proveedor
  // falló (ej. imagen de referencia que no se pudo procesar) — texto primero
  // para dar un error claro en vez del críptico "Unexpected end of JSON".
  const rawText = await res.text().catch(() => '');
  let json: any = null;
  if (rawText.trim()) {
    try {
      json = JSON.parse(rawText);
    } catch {
      throw new GenerateAdError(
        'technical',
        'El servidor de generación devolvió una respuesta inválida. Reintentá en un momento.',
        'invalid_json',
      );
    }
  }

  if (res.status === 402) {
    throw new GenerateAdError(
      'billing',
      mapErrorTypeToMessage('billing'),
      json?.error_message ?? 'billing',
    );
  }
  if (!res.ok) {
    // Atlas manda error_type tipado (censura/timeout/técnico) + mensaje ya
    // mostrable — respetarlo para que isActionable funcione (n8n: sin tipo).
    const t = (json?.error_type as GenerateAdErrorType) || 'technical';
    throw new GenerateAdError(
      t,
      json?.error_message || json?.error || mapErrorTypeToMessage(t),
      `HTTP ${res.status}`,
    );
  }
  if (json == null) {
    throw new GenerateAdError(
      'technical',
      'El servidor de generación no devolvió respuesta. Probablemente falló el procesamiento (imagen de referencia o parámetros). Reintentá o probá con otra imagen.',
      'empty_body',
    );
  }
  if (json?.response !== 'success' || (!json?.path && !json?.video_id)) {
    throw new GenerateAdError(
      'technical',
      json?.error_message || json?.error || 'La generación de video falló.',
      'unexpected_response',
    );
  }
  return {
    path: (json.path as string) ?? null,
    videoId: json.video_id != null ? String(json.video_id) : null,
    pending: false,
  };
}
